import { remoteConfig, RemoteConfigKeys } from '../services/remoteConfigService';

const CORS_PROXY = 'https://cors-612299373064.asia-south1.run.app';


/**
 * Wraps a URL with the CORS proxy on any non-localhost host.
 * Live dashboard (dashboard.farmvest.in) and staging both need the proxy.
 * Only localhost/127.0.0.1 calls the API directly.
 */
export function corsProxiedUrl(url: string): string {
  const hostname = window.location.hostname;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  return !isLocal ? `${CORS_PROXY}/${url}` : url;
}

export const API_CONFIG = {
  getEnvironment: () => {
    const hostname = window.location.hostname;

    // Environment variable override from build process (e.g., REACT_APP_TAG=staging)
    const buildEnv = (process.env.REACT_APP_TAG || process.env.REACT_APP_ENV || '').toLowerCase();

    // 1. If build-time says staging/dev, force dev mode
    if (buildEnv.includes('staging') || buildEnv.includes('dev') || buildEnv.includes('stagging')) {
      return 'dev';
    }

    // 2. If it's localhost or a known dev/staging/test domain, use dev mode
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDevDomain = hostname.includes('dev') ||
      hostname.includes('staging') ||
      hostname.includes('stagging') ||
      hostname.includes('stage') ||
      hostname.includes('test');


    if (isLocal || isDevDomain) {
      return 'dev';
    }

    // 3. Otherwise default to 'live' for production (mainlive)
    return 'live';
  },
  getFarmVestBaseUrl: () => {
    const mode = API_CONFIG.getEnvironment();

    // Use local backend when REACT_APP_LOCAL_FARMVEST_URL is set (dev/testing only)
    const localUrl = process.env.REACT_APP_LOCAL_FARMVEST_URL;
    if (localUrl && (mode === 'dev' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      return localUrl.replace(/\/$/, '');
    }

    // Remote values
    const liveUrl = remoteConfig.getValue(RemoteConfigKeys.LIVE_API_URL);
    const stagingUrl = remoteConfig.getValue(RemoteConfigKeys.STAGING_API_URL);

    let baseUrl = (mode === 'live') ? liveUrl : stagingUrl;
    baseUrl = (baseUrl || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
    return corsProxiedUrl(baseUrl);
  },
  getBaseUrl: () => {
    const mode = API_CONFIG.getEnvironment();

    // Remote values for AnimalKart
    const liveUrl = remoteConfig.getValue(RemoteConfigKeys.ANIMALKART_LIVE_URL);
    const stagingUrl = remoteConfig.getValue(RemoteConfigKeys.ANIMALKART_STAGING_URL);

    const baseUrl = mode === 'live' ? liveUrl : stagingUrl;
    return corsProxiedUrl((baseUrl || '').replace(/\/$/, ''));
  },
  getAnimalKartToken: () => {
    return remoteConfig.getValue(RemoteConfigKeys.ANIMALKART_API_TOKEN)
      || process.env.REACT_APP_ANIMALKART_TOKEN
      || 'mwbp-9f2a4e6b8d1c3f5a7b0e2d4c6f8a1b3c5e7d9f2a4b6c8e0d1f3a5b7c9e2f4a6';
  },
  getAnimalKartAesKey: () => {
    return remoteConfig.getValue(RemoteConfigKeys.ANIMALKART_AES_KEY)
      || process.env.REACT_APP_AES_RESPONSE_KEY
      || '';
  },
  getNotificationBaseUrl: () => {
    const liveUrl = remoteConfig.getValue(RemoteConfigKeys.NOTIFICATION_LIVE_URL) || 'https://notification-service-612299373064.asia-south1.run.app';
    return corsProxiedUrl(liveUrl.replace(/\/$/, ''));
  },
  getOdooBaseUrl: () => {
    return 'https://odoo-staging-inventory-612299373064.asia-south1.run.app';
  }
};

export const API_ENDPOINTS = {
  // AnimalKart Endpoints
  getUsers: () => `${API_CONFIG.getBaseUrl()}/users/customers`,
  getAdminsList: () => `${API_CONFIG.getBaseUrl()}/users/admins/list`,
  getReferrals: () => `${API_CONFIG.getBaseUrl()}/users/referrals`,
  createUser: () => `${API_CONFIG.getBaseUrl()}/users/referral-signup`,
  getUserDetails: (mobile: string) => `${API_CONFIG.getBaseUrl()}/users/${mobile}`,
  verifyUser: () => `${API_CONFIG.getBaseUrl()}/users/verify`,
  updateUser: (mobile: string) => `${API_CONFIG.getBaseUrl()}/users/${mobile}`,
  getProducts: () => `${API_CONFIG.getBaseUrl()}/products`,
  addProduct: () => `${API_CONFIG.getBaseUrl()}/products`,
  updateProduct: (id: string) => `${API_CONFIG.getBaseUrl()}/products/${id}`,
  deleteProduct: (id: string) => `${API_CONFIG.getBaseUrl()}/products/${id}`,
  health: () => `${API_CONFIG.getBaseUrl()}/health`,
  getPendingUnits: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/pending`,
  approveUnit: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/approve`,
  rejectUnit: () => `${API_CONFIG.getBaseUrl()}/purchases/admin/units/reject`,
  uploadProductImage: (id: string) => `${API_CONFIG.getBaseUrl()}/products/${id}/images`,
  getOrderStatus: () => `${API_CONFIG.getBaseUrl()}/order-tracking/stages`,
  updateOrderStatus: () => `${API_CONFIG.getBaseUrl()}/order-tracking/procurement/action`,
  getProcurementRoster: () => `${API_CONFIG.getBaseUrl()}/order-tracking/procurement/roster?stage=READY_TO_DELIVER`,
  getInvestorDetailsByOrder: (orderId: string) => `${API_CONFIG.getBaseUrl()}/purchases/units/order-details?orderId=${encodeURIComponent(orderId)}`,
  getOrderDetails: () => `${API_CONFIG.getBaseUrl()}/order-tracking/stages`,
  markInTransit: () => `${API_CONFIG.getBaseUrl()}/order-tracking/intransit`,
  getInTransitOrders: () => `${API_CONFIG.getBaseUrl()}/order-tracking/intransit`,
  getRoster: () => `${API_CONFIG.getBaseUrl()}/order-tracking/procurement/roster`,
  gettOrderDetails: () => `${API_CONFIG.getBaseUrl()}/purchases/units/order-details`,

  // FarmVest Endpoints
  // Auth
  staticLogin: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/auth/static_login`,
  loginWithOtp: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/auth/token`,
  adminLogin: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/auth/admin-login`,
  refreshToken: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/auth/refresh`,
  sendWhatsappOtp: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/auth/send-whatsapp-otp`,
  getMleStatus: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/auth/mle-status`,

  // Users
  getFarmVestUserData: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/users/get_user_data`, // Query params handled by caller
  updateFarmVestUserDetails: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/users/update_user_details/`,
  activateDeactivateUser: (mobile: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/users/activate_deactivate_user/${mobile}`,

  // Animals
  onboardAnimal: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/onboard_animal`,
  saveAnimalDraft: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/draft/save`,
  getAnimalDraft: (animalkart_order_id: string, investor_mobile: string, animalkart_buffalo_id?: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/draft?animalkart_order_id=${encodeURIComponent(animalkart_order_id)}&investor_mobile=${encodeURIComponent(investor_mobile)}${animalkart_buffalo_id ? `&animalkart_buffalo_id=${encodeURIComponent(animalkart_buffalo_id)}` : ''}`,
  allocateAnimal: (shedId: string | number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/shed_allocation/${shedId}`,
  getUnallocatedAnimals: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/unallocated_animals`,
  searchAnimal: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/search_animal`,
  getTotalAnimals: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/get_total_animals`,
  getAnimalByPosition: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/get-position`,
  getCalves: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/get_calves`,
  aiEntry: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/ai_entry`,
  getAnimalHistory: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/history`,


  quarantineLookup: (animalkart_buffalo_id: string, animalkart_order_id?: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/quarantine-lookup?animalkart_buffalo_id=${encodeURIComponent(animalkart_buffalo_id)}${animalkart_order_id ? `&animalkart_order_id=${encodeURIComponent(animalkart_order_id)}` : ''}`,

  // Employees
  createEmployee: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/employee/create_employee`,
  getAllEmployees: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/employee/get_all_employees`,
  getEmployeeDetailsById: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/employee/get_employee_details_by_id`,
  searchEmployee: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/employee/search_employee`,
  updateEmployee: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/employee/update_employee`,
  exportEmployeeData: (year?: number, month?: number) => {
    const base = `${API_CONFIG.getFarmVestBaseUrl()}/api/employee/export`;
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (month) params.set('month', String(month));
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  },

  // Farms
  createFarm: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/farm/farm`,
  getAllFarms: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/farm/get_all_farms`,
  getFarmDetails: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/farm/farm/details`,
  getFarmStaff: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/farm/staff`,
  getInvestorFarms: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/farm/investor/farms`,
  getFarmLocations: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/farm/locations`,
  createLocation: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/locations/`,
  updateFarm: (farmId: number | string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/farm/update/${farmId}`,
  deleteFarm: (farmId: number | string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/farm/delete/${farmId}`,

  // Investors
  getInvestorSummary: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/investors/summary`,
  getAdminSummary: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/investors/admin-summary`,
  getInvestorAnimals: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/investors/animals`,
  getAllInvestors: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/investors/get_all_investors`,
  getInvestorMouHtml: (investorId: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/investors/${investorId}/mou/html`,
  getInvestorMouPdf: (investorId: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/investors/${investorId}/mou/pdf`,

  // Leave Requests
  createLeaveRequest: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/leave_requests/create_leave_request`,
  getLeaveRequests: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/leave_requests/leave-requests`,
  updateLeaveStatus: (leaveId: number | string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/leave_requests/update_leave_status/${leaveId}`,
  cancelLeaveRequest: (leaveId: number | string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/leave_requests/leave-requests/${leaveId}`,

  // Unit Transfer Requests
  getPendingTransferTickets: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/transfer/pending-transfer-tickets`,
  approveUnitTransfer: (ticketId: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/transfer/approve-transfer-ticket/${ticketId}`,
  rejectUnitTransfer: (ticketId: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/transfer/reject-transfer-ticket/${ticketId}`,
  getOrderTransferHistory: (orderId: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/transfer/order-transfer-history/${orderId}`,

  // Milk
  createMilkEntry: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/milk/create_milk_entry`,
  getAllMilkEntries: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/milk/milk_entries`,
  getMilkReport: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/milk/get_milk_report`,

  // Sheds
  createShed: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/shed/create_shed`,
  listSheds: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/shed/list`,
  getAvailablePositionsInShed: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/shed/available_positions`,

  // Tickets
  createTicket: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/ticket/`,
  getHealthTickets: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/ticket/get_health_tickets`,

  // Visits
  bookVisitSlot: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/visits/book_visit_slot`,
  getAvailableVisitSlots: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/visits/available_visit_slots`,
  getMyVisits: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/visits/my-visits`,
  getAllVisits: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/visits/get_all_visits`,
  scanVisitQr: (visitId: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/visits/visits/${visitId}`,

  // Doctor
  getMyAssistants: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/doctor/get_my_assistants`,
  assignTickets: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/doctor/assign_tickets`,
  updateTreatmentDetails: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/doctor/update_treatment_details`,
  deleteEmployee: (id: number | string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/admin/delete_employee/${id}`,
  // Notifications
  getNotificationHistory: () => `${API_CONFIG.getNotificationBaseUrl()}/notifications/get-neckband-alerts`,
  getNotificationsHistory: (page: number, size: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/notifications/history?page=${page}&size=${size}`,
  getNotificationsUnreadCount: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/notifications/unread-count`,
  markNotificationsRead: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/notifications/mark-read`,
  getAlertRules: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/notifications/alert-rules`,
  updateAlertRule: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/notifications/alert-rules`,
  resetAlertRule: (alertType: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/notifications/alert-rules/${encodeURIComponent(alertType)}`,
  removeAlertRuleRole: (alertType: string, role: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/notifications/alert-rules/${alertType}?role=${role}`,

  // Animal Related Users (Lookup & Management)
  getAnimalRelatedUsers: (neckbandId: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/related_users/${neckbandId}`,
  addAnimalRelatedUser: (neckbandId: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/related_users/${neckbandId}`,
  removeAnimalRelatedUser: (neckbandId: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/related_users/${neckbandId}`,

  // Wallet
  getWalletBalance: (userId: number | string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/wallet/${userId}`,
  getWalletTransactions: (userId: number | string, limit = 50) => `${API_CONFIG.getFarmVestBaseUrl()}/api/wallet/${userId}/transactions?limit=${limit}`,
  getWithdrawalHistory: (userId: number | string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/wallet/withdraw/history/${userId}`,
  getAllWithdrawals: (status?: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/wallet/withdraw/all${status ? `?status=${status}` : ''}`,
  creditWallet: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/wallet/credit`,
  updatePayoutStatus: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/wallet/withdraw/payout-update`,
  // Payments
  getPendingPayments: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/payments/pending`,

  // Revenue
  getRevenueSummary: (month: number, year: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/summary?month=${month}&year=${year}`,
  getDuePayouts: (date?: string) => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/due-payouts${date ? `?date=${date}` : ''}`,
  getAllPendingPayouts: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/due-payouts/pending-all`,
  exportDuePayouts: (date?: string, toDate?: string, status?: string) => {
    const base = `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/due-payouts/export`;
    const params = new URLSearchParams();
    if (toDate && date) {
      params.set('from_date', date);
      params.set('to_date', toDate);
    } else if (date) {
      params.set('date', date);
    }
    if (status) params.set('status', status.toLowerCase());
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  },
  verifyPayouts: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/verify`,
  confirmPayment: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/confirm-payment`,
  triggerRevenueGeneration: (month: number, year: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/trigger-generation?month=${month}&year=${year}`,
  getInvestorMonthlyRevenue: (investorId: number, month: number, year: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/investor/${investorId}/monthly?month=${month}&year=${year}`,
  bulkVerifyRevenue: (month: number, year: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/bulk-verify?month=${month}&year=${year}`,
  bulkConfirmRevenue: (month: number, year: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/bulk-confirm?month=${month}&year=${year}`,
  exportRevenueCsv: (month: number, year: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/export?month=${month}&year=${year}`,
  getRevenueTrend: (months: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/trend?months=${months}`,
  getRevenueGenerationPreview: (month: number, year: number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/revenue/generation-preview?month=${month}&year=${year}`,
  updateAnimal: (animal_id: string | number) => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/${animal_id}`,
  getPendingOnboarding: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/pending-onboarding`,
  quarantineAssign: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/quarantine-assign`,
  getAssetValues: () => `${API_CONFIG.getFarmVestBaseUrl()}/api/animal/asset-values`,
};
